import { fromUnixTime } from "date-fns";
import { AuthType, Token } from "@/types";
import { createFutureAuthClient } from "@/futureAuth/authClient";
import config from "@/config/index";
import type { Client as FutureAuthClient } from "@fabriq/auth-client";
import { buildAuthTokenRepo } from "@/futureAuth/storageRepositories";
import { localLogger } from "@/utils/localLogger";
import { AuthenticationFailedException } from "./errors";

function extractParamFromUrl(
  url: string,
  param: string,
  reportSentry: (error: Error, data?: Record<string, any>) => void | null
) {
  try {
    const urlParams = new URL(url).searchParams;
    return urlParams.get(param) || undefined;
  } catch (e) {
    console.error(e);
    if (!reportSentry) {
      return;
    }
    if (e instanceof Error) {
      reportSentry(e, { url, param });
    }
  }
}

interface UserAttribute {
  Name: string;
  Value: string;
}
interface AuthUser {
  Attributes: Array<UserAttribute>;
  Enabled: boolean;
  UserCreateDate: string;
  UserLastModifiedDate: string;
  UserStatus: string;
  Username: string;
}
interface CheckIfEmailExistsData {
  type: AuthType;
  user?: AuthUser;
  futureIdpStatus?: "external" | "internal";
  providerName?: string;
  futureSubdomain?: string;
}

export { CheckIfEmailExistsData };

export class FabriqAuth {
  #reportSentry: ((error: Error, data?: Record<string, any>) => void) | null =
    null;

  #subdomainUrl: string | null = null;
  #futureAuthClient: FutureAuthClient | null = null;

  #email: string | null = null;
  #password: string | null = null;

  setReportSentry(
    reportSentry: (error: Error, data?: Record<string, any>) => void
  ) {
    this.#reportSentry = reportSentry;
  }

  applySubDomainUrl(subdomainUrl: string) {
    this.#subdomainUrl = subdomainUrl;
    this.#futureAuthClient = createFutureAuthClient(this.#subdomainUrl);
  }

  #getFutureAuthTokens() {
    const tokenRepository = buildAuthTokenRepo();
    return tokenRepository.getAuthenticationTokens();
  }

  async checkCurrentSession() {
    if (this.#futureAuthClient && this.#subdomainUrl) {
      const user = await this.#futureAuthClient.getLoggedInUser();
      const token = await this.#getFutureAuthTokens();
      await localLogger.log(`FabriqAuth checkCurrentSession already connected`);
      return { token, user, future: true };
    } else {
      await localLogger.log(
        `FabriqAuth checkCurrentSession error : not future AUTH`
      );
    }
  }

  setCredentials(email: string, password: string) {
    this.#password = password;
    this.#email = email;
  }

  async login(email: string, password: string): Promise<any> {
    await localLogger.log("FabriqAuth login setCredentials");
    this.setCredentials(email, password);
    await localLogger.log("FabriqAuth login getToken");
    const token = await this.#getToken(email, password);
    await localLogger.log("FabriqAuth login getToken OK");
    return token;
  }

  async organizationAuthenticationConfig() {
    if (!this.#subdomainUrl || !this.#futureAuthClient) return;
    const organizationId = "";
    const response =
      await this.#futureAuthClient.getOrganizationAuthenticationConfig(
        organizationId
      );
    if (response.outcome === "success") {
      return response.authConfig;
    } else {
      throw new Error("Wrong credentials");
    }
  }

  async userIdpProfile(email: string) {
    if (!this.#subdomainUrl || !this.#futureAuthClient) return;
    const organizationId = "";
    const response = await this.#futureAuthClient.checkUserIdp(
      organizationId,
      email
    );
    if (response.outcome === "success") {
      return response.status;
    } else {
      throw new Error("Wrong credentials");
    }
  }

  async ssoAuthenticationUri() {
    if (!this.#subdomainUrl || !this.#futureAuthClient) return;
    const organizationId = "";
    const startAuthenticationResponse =
      await this.#futureAuthClient.startAuthentication(
        organizationId,
        config.ssoRedirectUri
      );
    if (startAuthenticationResponse.outcome === "authenticationAborted") {
      throw new Error("Wrong credentials");
    }
    const authId = this.#extractUrlParameter(
      startAuthenticationResponse.authenticationUrl,
      "auth_id"
    );
    if (!authId?.length) {
      throw new Error("Wrong credentials");
    }
    const samlAuthenticationUriResponse =
      await this.#futureAuthClient?.getSAMLAuthenticationUri({
        organizationId,
        authId,
        redirectUri: config.ssoRedirectUri,
      });
    if (samlAuthenticationUriResponse.outcome === "notProvided") {
      throw new Error("Wrong credentials");
    }
    return samlAuthenticationUriResponse.callbackUri;
  }

  async finalizeFutureAuth(
    authorizationCode: string
  ): Promise<Token | undefined> {
    if (!this.#subdomainUrl || !this.#futureAuthClient) return;
    const organizationId = "";
    const authenticationResponse =
      await this.#futureAuthClient.finalizeAuthentication(
        organizationId,
        authorizationCode,
        config.ssoRedirectUri
      );
    if (authenticationResponse.outcome === "authenticationFinalized") {
      const authorizationTokens = authenticationResponse.authenticationTokens;
      return {
        type: AuthType.Future,
        accessToken: authorizationTokens.access.token,
        refreshToken: authorizationTokens.refresh.token,
        expiresAt: fromUnixTime(authorizationTokens.access.payload.exp),
      };
    }
  }

  #extractUrlParameter(url: string, param: string) {
    const urlObject = new URL(url);
    return urlObject.searchParams.get(param);
  }

  reLogin(): Promise<any> {
    if (!this.#email || !this.#password) throw new Error("Wrong credentials");
    return this.#getToken(this.#email, this.#password);
  }

  #getToken(email: string, password: string) {
    return this.#loginFuture(email, password);
  }

  async futureAuthRefreshToken() {
    if (!this.#subdomainUrl || !this.#futureAuthClient) return;
    const organizationId = "";
    const response = await this.#futureAuthClient.refreshAuthorizationTokens(
      organizationId
    );
    if (response.outcome === "refreshed") {
      const authorizationTokens = response.authenticationTokens;
      return {
        type: AuthType.Future,
        accessToken: authorizationTokens.access.token,
        refreshToken: authorizationTokens.refresh.token,
        expiresAt: fromUnixTime(authorizationTokens.access.payload.exp),
      };
    } else {
      throw new Error("Refresh token error", { cause: response.reason });
    }
  }

  async #loginFuture(
    email: string,
    password: string
  ): Promise<Token | undefined> {
    if (!this.#subdomainUrl || !this.#futureAuthClient) return;
    const organizationId = "";
    const startAuthResult = await this.#futureAuthClient.startAuthentication(
      organizationId,
      config.ssoRedirectUri
    );

    if (startAuthResult.outcome !== "authenticationStarted")
      throw new AuthenticationFailedException("genericError", "Aborted");

    const authId = extractParamFromUrl(
      startAuthResult.authenticationUrl,
      "auth_id",
      this.#reportSentry
    );

    if (!authId)
      throw new AuthenticationFailedException("genericError", "No auth id");

    const idpAuthResult =
      await this.#futureAuthClient.authenticateWithIdentityProvider(
        authId,
        organizationId,
        {
          username: email,
          password,
        }
      );
    if (idpAuthResult.outcome !== "authenticationSucceeded") {
      throw new AuthenticationFailedException(
        "invalidCredentials",
        "Invalid credentials"
      );
    }

    const idpToken = extractParamFromUrl(
      idpAuthResult.callbackUrl,
      "token",
      this.#reportSentry
    );

    if (!idpToken)
      throw new AuthenticationFailedException("genericError", "no idp token");

    const authCodeResult =
      await this.#futureAuthClient.getAuthorizationCodeFromServer(
        organizationId,
        idpToken
      );

    if (authCodeResult.outcome !== "provided") {
      throw new AuthenticationFailedException(
        "genericError",
        "No auth code provided"
      );
    }

    const code = extractParamFromUrl(
      authCodeResult.callbackUrl,
      "code",
      this.#reportSentry
    );

    if (!code)
      throw new AuthenticationFailedException(
        "genericError",
        "No auth code found in url"
      );

    return this.finalizeFutureAuth(code);
  }

  async logout(token: Token | null) {
    if (!token) return;
    await this.#futureAuthClient?.logoutUser();
  }

  async logoutFuture() {
    await this.#futureAuthClient?.logoutUser();
  }
}
